import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Zap } from "lucide-react";
import { publicApi, useApi, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { DateTimeField } from "@/components/site/DateTimeField";
import { DurationDiscountsCard, cleanTiers } from "@/components/admin/DurationDiscountsCard";

const ddmmyyyy = (iso) => iso.split("-").reverse().join("/");

const inputCls = "rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";
let winSeq = 0;
const withKeys = (cfg) => ({ ...cfg, peak_windows: cfg.peak_windows.map((w) => ({ ...w, _k: ++winSeq })) });
const Card = ({ title, desc, children }) => (
  <div className="rounded-lg border border-white/10 bg-[#141414] p-5">
    <h2 className="text-lg font-semibold">{title}</h2>
    {desc && <p className="mt-1 text-sm text-neutral-400">{desc}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

export default function AdminPricing() {
  const api = useApi();
  const [p, setP] = useState(null);
  const [fest, setFest] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    publicApi.get("/pricing").then((r) => setP((cur) => cur ?? withKeys(r.data)));
  }, []);
  if (!p) return <p className="text-neutral-400">Loading…</p>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setWin = (i, k, v) => set("peak_windows", p.peak_windows.map((w, j) => (j === i ? { ...w, [k]: v } : w)));

  const save = async () => {
    if (p.surge_pct === "" || Number.isNaN(Number(p.surge_pct))) return toast.error("Enter a surcharge percentage (0–100)");
    const { tiers, error } = cleanTiers(p.duration_discounts || []);
    if (error) return toast.error(error);
    setBusy(true);
    try {
      const peak_windows = p.peak_windows.map(({ start, end }) => ({ start, end }));
      const r = await api.put("/admin/pricing", { ...p, peak_windows, duration_discounts: tiers, surge_pct: Number(p.surge_pct) });
      setP(withKeys(r.data));
      toast.success("Pricing rules saved — applied to all new quotes immediately");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const addFest = () => {
    if (!fest.date || !fest.name.trim()) return toast.error("Pick a date and enter the festival name");
    set("festivals", [...p.festivals.filter((f) => f.date !== fest.date), { date: fest.date, name: fest.name.trim() }].sort((a, b) => a.date.localeCompare(b.date)));
    setFest({ date: "", name: "" });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-pricing">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight"><Zap className="h-7 w-7 text-[#FF5A00]" />Dynamic pricing</h1>
        <p className="mt-1 text-sm text-neutral-400">Every booked hour that falls in a peak period is automatically charged extra. No approval needed — changes apply to new quotes instantly. Existing bookings keep their price.</p>
      </div>
      <Card title="Peak surcharge" desc="Extra charged on the car's hourly rate during peak hours.">
        <label className="flex items-center gap-3 text-sm">
          <input type="number" min="0" max="100" value={p.surge_pct} onChange={(e) => set("surge_pct", e.target.value)} data-testid="pricing-surge-pct" className={`${inputCls} w-24`} />
          % extra
        </label>
      </Card>
      <Card title="Weekends" desc="All of Saturday and Sunday (IST).">
        <label className="flex items-center gap-3 text-sm"><Switch checked={p.weekends} onCheckedChange={(v) => set("weekends", v)} data-testid="pricing-weekends" />Apply surcharge on weekends</label>
      </Card>
      <Card title="Daily peak hours" desc="Time windows (IST) treated as peak on every day. A window like 22:00 → 02:00 wraps past midnight.">
        <div className="space-y-2">
          {p.peak_windows.map((w, i) => (
            <div key={w._k} className="flex flex-wrap items-center gap-2" data-testid={`pricing-window-${i}`}>
              <input type="time" value={w.start} onChange={(e) => setWin(i, "start", e.target.value)} data-testid={`pricing-window-start-${i}`} className={inputCls} />
              <span className="text-neutral-500">to</span>
              <input type="time" value={w.end === "24:00" ? "23:59" : w.end} onChange={(e) => setWin(i, "end", e.target.value)} data-testid={`pricing-window-end-${i}`} className={inputCls} />
              <button onClick={() => set("peak_windows", p.peak_windows.filter((_, j) => j !== i))} data-testid={`pricing-window-remove-${i}`} className="rounded-md p-2 text-rose-400 hover:bg-white/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => set("peak_windows", [...p.peak_windows, { start: "08:00", end: "11:00", _k: ++winSeq }])} data-testid="pricing-window-add" className="flex items-center gap-1 text-sm text-[#FF5A00]"><Plus className="h-4 w-4" />Add time window</button>
        </div>
      </Card>
      <DurationDiscountsCard tiers={p.duration_discounts || []} onChange={(v) => set("duration_discounts", v)} />
      <Card title="Festival days" desc="Whole days (IST) charged at peak rates, e.g. Diwali, Christmas, long weekends.">
        <div className="flex flex-wrap gap-2">
          <DateTimeField withTime={false} testId="pricing-fest-date" value={fest.date} onChange={(v) => setFest({ ...fest, date: v })} className={inputCls} />
          <input placeholder="Festival name" value={fest.name} onChange={(e) => setFest({ ...fest, name: e.target.value })} data-testid="pricing-fest-name" className={`${inputCls} flex-1`} />
          <button onClick={addFest} data-testid="pricing-fest-add" className="rounded-md bg-white/10 px-4 text-sm font-semibold">Add</button>
        </div>
        <ul className="mt-3 divide-y divide-white/5">
          {p.festivals.length === 0 && <li className="py-2 text-sm text-neutral-500">No festival days added yet.</li>}
          {p.festivals.map((f) => (
            <li key={f.date} className="flex items-center justify-between py-2 text-sm" data-testid={`pricing-fest-${f.date}`}>
              <span><span className="font-mono text-neutral-400">{ddmmyyyy(f.date)}</span> · {f.name}</span>
              <button onClick={() => set("festivals", p.festivals.filter((x) => x.date !== f.date))} data-testid={`pricing-fest-remove-${f.date}`} className="rounded-md p-1.5 text-rose-400 hover:bg-white/10"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      </Card>
      <button onClick={save} disabled={busy} data-testid="pricing-save" className="rounded-md bg-[#FF5A00] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving…" : "Save pricing rules"}</button>
    </div>
  );
}
